import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { HeroCarIllustration } from '../components/CarCard';
import CarCard from '../components/CarCard';
import { Spinner, Button, Modal, Input, Textarea, Badge, formatPrice, formatMileage, formatDate, useToast, T } from '../components/UI';

const WA = '353851234567';

const SPEC_LABELS = {
  Year:'Year', Mileage:'Mileage', Fuel:'Fuel type', Body:'Body style',
  Transmission:'Transmission', Colour:'Colour', Engine:'Engine', Seats:'Seats', Doors:'Doors', NCT:'NCT expiry',
};

export default function CarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { show, ToastContainer } = useToast();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', phone:'', message:'' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setLoading(true);
    api.getListing(id)
      .then(data => {
        setCar(data);
        setForm(f => ({ ...f, message: `Hi, I'm interested in the ${data.year} ${data.make} ${data.model} at ${formatPrice(data.price)}. Is it still available?` }));
      })
      .catch(() => nav('/stock'))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id, nav]);

  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(er => ({ ...er, [k]:'' })); };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.message.trim()) errs.message = 'Please add a message';
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.sendEnquiry({ ...form, listingId: id, type: 'listing' });
      show("Message sent — we'll be in touch shortly.");
      setContactOpen(false);
    } catch { show('Failed to send. Please call us.', 'error'); }
    setSending(false);
  };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', paddingTop: 180 }}><Spinner size={40} /></div>;
  if (!car) return null;

  const specs = [
    { label: 'Year',         value: car.year },
    { label: 'Mileage',      value: formatMileage(car.mileage) },
    { label: 'Fuel',         value: car.fuelType },
    { label: 'Body',         value: car.bodyType },
    { label: 'Transmission', value: car.transmission },
    { label: 'Colour',       value: car.colour },
    { label: 'Engine',       value: car.engine },
    { label: 'Seats',        value: car.seats },
    { label: 'Doors',        value: car.doors },
    { label: 'NCT',          value: car.nct },
  ];

  const waMsg = encodeURIComponent(`Hi Northbridge, I'm interested in the ${car.year} ${car.make} ${car.model} — ${formatPrice(car.price)}. Is it available?`);
  const available = car.status === 'available';

  return (
    <div style={{ paddingTop: 68, background: T.bg, minHeight: '100vh' }}>
      <ToastContainer />

      {/* Breadcrumb */}
      <div style={{ background: T.surface, paddingBlock: 12, paddingInline: 'var(--margin)', borderBottom: `1px solid ${T.surface}` }}>
        <nav style={{ maxWidth: 1680, margin: '0 auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.03em' }}>
          <Link to="/" style={{ color: T.muted, transition: 'color 0.15s' }} onMouseEnter={e=>e.target.style.color=T.accent} onMouseLeave={e=>e.target.style.color=T.muted}>Home</Link>
          <span style={{ color: T.surface }}>›</span>
          <Link to="/stock" style={{ color: T.muted, transition: 'color 0.15s' }} onMouseEnter={e=>e.target.style.color=T.accent} onMouseLeave={e=>e.target.style.color=T.muted}>Stock</Link>
          <span style={{ color: T.surface }}>›</span>
          <span style={{ color: T.ink }}>{car.make} {car.model}</span>
        </nav>
      </div>

      <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', paddingBottom: 80 }}>

        {/* ── Hero split: illustration left, details right ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 40, alignItems: 'start', paddingTop: 32 }}>

          {/* LEFT */}
          <div>
            {/* Car illustration panel */}
            <div style={{ background: T.ink, position: 'relative', overflow: 'hidden', marginBottom: 2 }}>
              {/* atmosphere photo behind */}
              <img src="https://picsum.photos/id/955/1200/700" alt="" loading="lazy" width="1200" height="700"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18, display: 'block' }} />
              {/* UK flag top strip */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg,#012169 33%,#fff 33%,#fff 34%,#C8102E 34%,#C8102E 66%,#fff 66%,#fff 67%,#012169 67%)', zIndex: 2 }} />
              {/* Badges */}
              <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 6, zIndex: 2 }}>
                <Badge variant="uk">🇬🇧 UK Import</Badge>
                {!available && <Badge variant="sold" style={{ background: T.muted }}>{car.status === 'sold' ? 'Sold' : 'Reserved'}</Badge>}
                {available && (Date.now() - new Date(car.publishedAt).getTime()) < 7*24*60*60*1000 && <Badge variant="gold">Just In</Badge>}
              </div>
              {/* Views */}
              <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.5)', zIndex: 2, letterSpacing: '0.06em' }}>{car.views} views</div>
              {/* Illustration centred */}
              <div style={{ position: 'relative', zIndex: 1, padding: '56px 40px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
                <HeroCarIllustration bodyType={car.bodyType} colour={car.colour} />
                {/* Status overlay */}
                {!available && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,25,22,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
                    <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,5vw,4rem)', fontWeight: 400, color: T.bg, letterSpacing: '0.1em', textTransform: 'uppercase', border: `1px solid ${T.bg}`, padding: '10px 40px' }}>
                      {car.status === 'sold' ? 'Sold' : 'Reserved'}
                    </span>
                  </div>
                )}
              </div>
              {/* Colour swatch strip */}
              <div style={{ position: 'absolute', bottom: 16, left: 16, display: 'flex', alignItems: 'center', gap: 8, zIndex: 2 }}>
                <div style={{ width: 20, height: 20, background: { Black:'#1a1916', White:'#f5f3ef', Silver:'#a8a8a8', Grey:'#787878', Blue:'#1e3a5f', Red:'#8b1c1c', Green:'#1a3a1a', Orange:'#b45309', Yellow:'#b45309', Brown:'#5c3d1e', Beige:'#c8b89a' }[car.colour] || '#888', border: '1px solid rgba(232,230,225,0.3)' }} />
                <span style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.6)', letterSpacing: '0.06em' }}>{car.colour}</span>
              </div>
            </div>

            {/* Title + price */}
            <div style={{ background: T.surface, padding: '28px 32px', marginBottom: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    {car.bodyType} · {car.fuelType} · {car.transmission}
                  </p>
                  <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 400, color: T.ink, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                    {car.year} {car.make} {car.model}
                  </h1>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 400, color: T.ink, lineHeight: 1 }}>
                    {formatPrice(car.price)}
                  </div>
                  {car.monthlyPrice > 0 && <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginTop: 6, letterSpacing: '0.04em' }}>Finance from {formatPrice(car.monthlyPrice)}/mo</div>}
                </div>
              </div>
            </div>

            {/* Specs grid */}
            <div style={{ background: T.bg, border: `1px solid ${T.surface}`, padding: '28px 32px', marginBottom: 2 }}>
              <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Specifications</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 0, border: `1px solid ${T.surface}`, borderRight: 'none', borderBottom: 'none' }}>
                {specs.filter(s => s.value).map(({ label, value }) => (
                  <div key={label} style={{ padding: '14px 16px', borderRight: `1px solid ${T.surface}`, borderBottom: `1px solid ${T.surface}` }}>
                    <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{SPEC_LABELS[label] || label}</div>
                    <div style={{ fontSize: 'var(--step-sm)', fontWeight: 600, color: T.ink }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            {car.features?.length > 0 && (
              <div style={{ background: T.bg, border: `1px solid ${T.surface}`, padding: '28px 32px', marginBottom: 2 }}>
                <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 18 }}>Features & Options</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {car.features.map(f => (
                    <span key={f} style={{ border: `1px solid ${T.surface}`, padding: '6px 14px', fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: T.accent, fontSize: 10 }}>✓</span> {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div style={{ background: T.bg, border: `1px solid ${T.surface}`, padding: '28px 32px', marginBottom: 2 }}>
              <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 18 }}>About this car</p>
              <p style={{ fontSize: 'var(--step-body)', color: T.ink, lineHeight: 1.8 }}>{car.description}</p>
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${T.surface}`, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: T.accent, fontSize: 14, flexShrink: 0, marginTop: 2 }}>✓</span>
                <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: T.ink, fontWeight: 600 }}>Northbridge Guarantee:</strong> Full Cartell history check, 30-day mechanical warranty, NCT prepared before handover.
                </p>
              </div>
            </div>

            {/* Buying tips */}
            <div style={{ background: '#fffbeb', border: `1px solid #fde68a`, padding: '20px 28px', marginBottom: 32 }}>
              <p style={{ fontSize: 'var(--step-xs)', fontWeight: 700, color: '#92400e', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Buying tips</p>
              {['Always view the car in person at our Dublin forecourt', 'Never pay a deposit without a signed agreement', 'Ask to see the full Cartell history report', 'Test drive before you commit'].map((t, i) => (
                <p key={i} style={{ fontSize: 'var(--step-sm)', color: '#92400e', margin: '4px 0', display: 'flex', gap: 8 }}><span>—</span>{t}</p>
              ))}
            </div>

            {/* Similar cars */}
            {car.similar?.length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${T.surface}` }}>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 400, color: T.ink }}>More {car.make}s</h2>
                  <Link to={`/stock?make=${car.make}`} style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.04em', fontWeight: 500 }}>See all →</Link>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 2 }}>
                  {car.similar.map(c => <CarCard key={c.id} car={c} />)}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — sticky contact panel */}
          <div style={{ position: 'sticky', top: 84, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Price & CTA */}
            <div style={{ background: T.surface, padding: '28px 24px' }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, color: T.ink, lineHeight: 1, marginBottom: 6 }}>
                {formatPrice(car.price)}
              </div>
              {car.monthlyPrice > 0 && (
                <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginBottom: 24, letterSpacing: '0.04em' }}>
                  Finance from {formatPrice(car.monthlyPrice)}/mo
                </div>
              )}

              {available ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Button fullWidth size="lg" onClick={() => setContactOpen(true)}>Enquire about this car</Button>
                  <a href={`https://wa.me/${WA}?text=${waMsg}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#25D366', color: '#fff', padding: '13px 28px', fontSize: 'var(--step-xs)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', transition: 'opacity 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.opacity='0.85'}
                    onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.855L.054 23.946l6.283-1.649A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm5.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
                    WhatsApp us
                  </a>
                  <a href="tel:+353851234567">
                    <Button fullWidth variant="outline">Call +353 85 123 4567</Button>
                  </a>
                </div>
              ) : (
                <div style={{ textAlign: 'center', paddingBlock: 16 }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', color: T.muted, fontStyle: 'italic', marginBottom: 12 }}>
                    {car.status === 'sold' ? 'This car has been sold' : 'This car is reserved'}
                  </div>
                  <Link to="/stock" style={{ fontSize: 'var(--step-sm)', color: T.accent, fontWeight: 500, letterSpacing: '0.04em' }}>Browse similar cars →</Link>
                </div>
              )}
            </div>

            {/* Finance box */}
            {available && car.price > 6000 && (
              <div style={{ background: T.ink, padding: '24px 24px', color: T.bg }}>
                <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Finance available</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.5)', letterSpacing: '0.04em' }}>Monthly from</span>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-head)', fontWeight: 400, color: T.bg, lineHeight: 1 }}>{formatPrice(car.monthlyPrice)}</span>
                </div>
                <p style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.35)', marginBottom: 18, letterSpacing: '0.04em' }}>48 months · 7.9% APR representative</p>
                <Button fullWidth variant="primary" onClick={() => setContactOpen(true)}>Apply for Finance</Button>
              </div>
            )}

            {/* Dealer info */}
            <div style={{ background: T.surface, padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${T.bg}` }}>
                <div style={{ width: 40, height: 40, background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 13 }}>NA</span>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--step-sm)', color: T.ink }}>Northbridge Automotives</div>
                  <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>Dublin, Ireland · UK Imports</div>
                </div>
              </div>
              {[['📍','Dublin forecourt — view by appointment'],['🕐','Mon–Sat 9am–6pm'],['📋','Full Cartell history included'],['🛡️','30-day warranty on all cars']].map(([icon,text]) => (
                <div key={text} style={{ display: 'flex', gap: 10, fontSize: 'var(--step-xs)', color: T.muted, padding: '5px 0', letterSpacing: '0.02em' }}>
                  <span>{icon}</span>{text}
                </div>
              ))}
            </div>

            <div style={{ fontSize: 'var(--step-xs)', color: T.muted, textAlign: 'center', padding: '10px 0', letterSpacing: '0.06em' }}>
              Listed {formatDate(car.publishedAt)}
            </div>
          </div>
        </div>
      </div>

      {/* Enquiry modal */}
      <Modal open={contactOpen} onClose={() => setContactOpen(false)} title={`Enquire — ${car.year} ${car.make} ${car.model}`}>
        <div style={{ background: T.surface, padding: '12px 16px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Price</span>
          <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', fontWeight: 400, color: T.ink }}>{formatPrice(car.price)}</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Your name *" placeholder="Seán Murphy" value={form.name} onChange={set('name')} error={errors.name} />
              <Input label="Phone" placeholder="+353 85 …" value={form.phone} onChange={set('phone')} type="tel" />
            </div>
            <Input label="Email *" placeholder="you@example.com" value={form.email} onChange={set('email')} type="email" error={errors.email} />
            <Textarea label="Message *" value={form.message} onChange={set('message')} error={errors.message} style={{ minHeight: 120 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="outline" onClick={() => setContactOpen(false)} type="button">Cancel</Button>
              <Button type="submit" fullWidth loading={sending}>Send Enquiry</Button>
            </div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em', textAlign: 'center' }}>We reply within 1 hour during business hours.</p>
          </div>
        </form>
      </Modal>
    </div>
  );
}
